/**
 *  @class
 *  @function SwiperCarousel
 *  Flickity-compatible facade: insert, reloadCells, select, SwiperCarousel.data
 */
if (!customElements.get("swiper-carousel")) {
	class SwiperCarousel extends HTMLElement {
		constructor() {
			super();
			this.swiper = null;
			this._destroyed = false;
			this._boundInit = this.initWhenReady.bind(this);
		}
		static data(el) {
			return el && el.swiperCarousel ? el.swiperCarousel : null;
		}
		connectedCallback() {
			this._destroyed = false;
			this.swiperCarousel = this;
			this.wrapper = this.querySelector(".swiper-wrapper");
			this.pool = this.querySelector(".swiper-carousel__pool");
			this.progressBar =
				this.parentNode && this.parentNode.querySelector(".swiper-carousel__progress-bar");
			this.prevButton = this.querySelector(".swiper-carousel__nav--prev");
			this.nextButton = this.querySelector(".swiper-carousel__nav--next");
			this.addEventListener("lazyloaded", this._onLazy, true);
			this.initWhenReady();
		}
		disconnectedCallback() {
			this._destroyed = true;
			document.removeEventListener("DOMContentLoaded", this._boundInit);
			window.removeEventListener("load", this._boundInit);
			this.removeEventListener("lazyloaded", this._onLazy, true);
			this.destroy();
		}
		initWhenReady() {
			if (this._destroyed) {
				return;
			}
			if (typeof Swiper !== "undefined") {
				this.init();
				return;
			}
			document.addEventListener("DOMContentLoaded", this._boundInit, { once: true });
			window.addEventListener("load", this._boundInit, { once: true });
		}
		init() {
			if (this.swiper || this._destroyed || typeof Swiper === "undefined") {
				return;
			}

			const container = this.querySelector(".swiper");
			if (!container) {
				return;
			}

			const slidesMobile = parseInt(this.dataset.slidesMobile, 10) || 2;
			const slidesDesktop = parseInt(this.dataset.slidesDesktop, 10) || 4;
			const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 20;
			const carousel = this;

			const args = {
				slidesPerView: slidesMobile,
				slidesPerGroup: 1,
				spaceBetween: spaceBetween,
				loop: false,
				centeredSlides: false,
				watchOverflow: true,
				observer: true,
				observeParents: true,
				grabCursor: true,
				preventClicks: true,
				preventClicksPropagation: false,
				rtl: document.dir === "rtl",
				breakpoints: {
					768: {
						slidesPerView: slidesDesktop,
						spaceBetween: spaceBetween,
					},
				},
				on: {
					init: function () {
						carousel.updateProgress(this.progress);
					},
					progress: function (swiper, progress) {
						carousel.updateProgress(progress);
					},
				},
			};

			if (this.prevButton && this.nextButton) {
				args.navigation = {
					prevEl: this.prevButton,
					nextEl: this.nextButton,
				};
			}

			try {
				this.swiper = new Swiper(container, args);
				this.setAttribute("data-initialized", "");
			} catch (error) {
				console.error("Swiper carousel initialization error:", error);
			}
		}
		destroy() {
			if (this.swiper && typeof this.swiper.destroy === "function") {
				this.swiper.destroy(true, true);
			}
			this.swiper = null;
			this.removeAttribute("data-initialized");
		}
		insert(els) {
			if (!this.wrapper) {
				this.wrapper = this.querySelector(".swiper-wrapper");
			}
			if (!this.wrapper || !els) {
				return;
			}
			const nodes = els.length !== undefined ? Array.from(els) : [els];
			nodes.forEach((el) => {
				if (el) {
					this.wrapper.append(el);
				}
			});
		}
		reloadCells() {
			if (this.swiper) {
				this.swiper.update();
			}
			if (window.lazySizes && lazySizes.loader) {
				lazySizes.loader.checkElems();
			}
		}
		select(index, wrap, instant) {
			if (!this.swiper) {
				return;
			}
			const speed = instant ? 0 : undefined;
			this.swiper.slideTo(index || 0, speed);
		}
		updateProgress(progress) {
			if (!this.progressBar) {
				return;
			}
			const value = Math.max(0, Math.min(1, progress));
			this.progressBar.style.width = value * 100 + "%";
		}
	}
	customElements.define("swiper-carousel", SwiperCarousel);
}
